'use client'

import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Lottie } from 'lottie-react'

// 4 triangles from the brand-loader Lottie (brand-loader.json)
// Coordinates derived from Lottie path vertices + group transforms at scale 1
// viewBox "0 0 52 64" — logo fills this box; SVG element is sized independently
const TRIANGLES = [
  // top-left (layer st:0)
  'M 0.215,0 L 16.165,27.812 L 32.297,0.099 Z',
  // top-right (layer st:1)
  'M 19.768,29.893 L 51.852,30.009 L 35.9,2.197 Z',
  // bottom-right (layer st:2)
  'M 19.752,34.07 L 35.702,61.882 L 51.836,34.169 Z',
  // bottom-left (layer st:3)
  'M 0,63.846 L 32.082,63.946 L 16.133,36.134 Z',
]

// Each triangle appears one frame apart at 3fps ≈ 0.33s stagger
const STAGGER = 1 / 3.03030395507812

// Brand-specific Lottie loaders — downloaded from Webflow's CDN into
// public/media/loaders so the loader doesn't depend on a third-party host
// at runtime. Lottie's `src` accepts a URL directly and fetches it lazily,
// so only the one actually needed for the current brand is ever loaded.
const BRAND_LOTTIE_SRC: Record<string, string> = {
  flow: '/media/loaders/flow-loader.json',
  gravity: '/media/loaders/gravity-loader.json',
}

export default function PageLoader({ brand }: { brand?: string }) {
  const [visible, setVisible] = useState(true)
  const lottieSrc = brand ? BRAND_LOTTIE_SRC[brand] : undefined

  useEffect(() => {
    // Stay visible until page is fully loaded, then wait for the last triangle
    // to settle before fading out. Cap at 3s total regardless.
    const dismiss = () => {
      const totalStagger = TRIANGLES.length * STAGGER * 1000
      setTimeout(() => setVisible(false), Math.min(totalStagger + 300, 2000))
    }

    if (document.readyState === 'complete') {
      dismiss()
    } else {
      window.addEventListener('load', dismiss, { once: true })
      const cap = setTimeout(() => setVisible(false), 3000)
      return () => clearTimeout(cap)
    }
  }, [])

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="page-loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.35, ease: 'easeOut' } }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {lottieSrc ? (
            <Lottie src={lottieSrc} loop autoplay style={{ width: 160, height: 160 }} />
          ) : (
            <svg
              width="72"
              height="88"
              viewBox="0 0 52 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {TRIANGLES.map((d, i) => (
                <motion.path
                  key={i}
                  d={d}
                  fill="#000a1b"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * STAGGER, duration: 0.08 }}
                />
              ))}
            </svg>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
